/**
 * Chart provider picker — the focused control behind the menu's "Chart provider"
 * leaf. Two tabs:
 *   - "Your charts": the installed providers, grouped "Available here" and "Other
 *     installed charts", favourites pinned. Tapping a row makes it the active
 *     provider. When Antares is the active provider, its download-on-demand packs
 *     are shown inline right here (view online / download / delete).
 *   - "More charts": the same Antares packs, plus previews of third-party sources
 *     that still need a backend (those stay stubs).
 */
import { useState } from 'react';
import { useChartPlotterStore } from '@/store';
import { useChartSourcesStore } from '@/store/chartSources';
import { useChartFavouritesStore } from '@/store/chartFavourites';
import { useShellStore } from '@/store/shell';
import {
  CHART_PROVIDERS,
  resolveProvider,
  isProviderAvailableAt,
  STORE_CHARTS,
  type ChartProvider,
  type StoreChart,
} from '@/chart-sources';
import { haptic } from '@/ui/haptics';
import { ChartPacksPanel } from './ChartPacksPanel';

function kindLabel(kind: ChartProvider['kind']): string {
  if (kind === 'tiles') return 'Base + seamarks';
  return kind === 'vector' ? 'Vector' : 'Raster';
}

export function ChartProviderPicker() {
  const [tab, setTab] = useState<'yours' | 'more'>('yours');
  const chartProviderId = useChartPlotterStore((s) => s.chartProviderId);
  const setChartProvider = useChartPlotterStore((s) => s.setChartProvider);
  const position = useChartPlotterStore((s) => s.ownship.position);
  const bsbCharts = useChartSourcesStore((s) => s.bsbCharts);
  const noaaCharts = useChartSourcesStore((s) => s.noaaCharts);
  const packCharts = useChartSourcesStore((s) => s.packCharts);
  const favIds = useChartFavouritesStore((s) => s.favIds);
  const toggleFavourite = useChartFavouritesStore((s) => s.toggleFavourite);
  const showToast = useShellStore((s) => s.showToast);

  const resolved = CHART_PROVIDERS.map((p) => resolveProvider(p.id, bsbCharts, noaaCharts, packCharts));
  const isFav = (id: string) => favIds.includes(id);
  const byFav = (a: ChartProvider, b: ChartProvider) =>
    (isFav(b.id) ? 1 : 0) - (isFav(a.id) ? 1 : 0);

  const available = position
    ? resolved.filter((p) => isProviderAvailableAt(p, position)).sort(byFav)
    : [];
  const availIds = new Set(available.map((p) => p.id));
  const others = resolved.filter((p) => !availIds.has(p.id)).sort(byFav);

  function selectProvider(p: ChartProvider) {
    haptic('tick');
    setChartProvider(p.id);
    showToast(`${p.name} selected`);
  }

  function installStub(sc: StoreChart) {
    haptic('tick');
    showToast(
      `${sc.name} — ${sc.action === 'subscribe' ? 'subscriptions' : 'chart downloads'} not available yet (coming soon)`,
    );
  }

  function ProviderRow({ p }: { p: ChartProvider }) {
    const active = chartProviderId === p.id;
    const fav = isFav(p.id);
    return (
      <div className={`acp-prov-row${active ? ' on' : ''}${fav ? ' fav' : ''}`}>
        <button
          type="button"
          className="acp-prov-main"
          aria-pressed={active}
          onClick={() => selectProvider(p)}
        >
          <b>{p.name}</b>
          <span>
            {p.short} · {kindLabel(p.kind)}
            {active ? ' · active' : ''}
          </span>
        </button>
        <button
          type="button"
          className={`acp-star${fav ? ' on' : ''}`}
          aria-pressed={fav}
          aria-label={fav ? `Unstar ${p.name}` : `Star ${p.name}`}
          onClick={() => {
            haptic('tick');
            toggleFavourite(p.id);
          }}
        >
          {fav ? '★' : '☆'}
        </button>
      </div>
    );
  }

  /** A provider row, plus the Antares packs inline when that row is the active provider. */
  function Row({ p }: { p: ChartProvider }) {
    const showPacks = p.id === 'bsb' && chartProviderId === 'bsb';
    return (
      <>
        <ProviderRow p={p} />
        {showPacks && (
          <div className="acp-inline-packs">
            <ChartPacksPanel heading={false} />
          </div>
        )}
      </>
    );
  }

  return (
    <div className="acp-chartsrc">
      <div className="acp-tabs">
        <button type="button" className={tab === 'yours' ? 'on' : ''} onClick={() => setTab('yours')}>
          Your charts
        </button>
        <button type="button" className={tab === 'more' ? 'on' : ''} onClick={() => setTab('more')}>
          More charts
        </button>
      </div>

      {tab === 'yours' ? (
        position ? (
          <>
            {available.length > 0 && (
              <>
                <div className="rgn">Available here</div>
                {available.map((p) => (
                  <Row key={p.id} p={p} />
                ))}
              </>
            )}
            <div className="rgn">Other installed charts</div>
            {others.map((p) => (
              <Row key={p.id} p={p} />
            ))}
          </>
        ) : (
          <>
            <span className="lead">
              No position yet, so all installed charts are listed together. Starred charts are pinned
              to the top.
            </span>
            {[...resolved].sort(byFav).map((p) => (
              <Row key={p.id} p={p} />
            ))}
          </>
        )
      ) : (
        <>
          <ChartPacksPanel />

          <div className="rgn">Other sources</div>
          <span className="lead">
            These still need a backend, so the buttons report back rather than charge or download.
          </span>
          {STORE_CHARTS.map((sc) => (
            <div className="acp-prov-row" key={sc.id}>
              <div className="acp-prov-main acp-prov-static">
                <b>{sc.name}</b>
                <span>
                  {sc.short} · {kindLabel(sc.kind)}
                </span>
              </div>
              <button type="button" className="acp-btn" onClick={() => installStub(sc)}>
                {sc.action === 'subscribe' ? 'Subscribe' : 'Install'}
              </button>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
